import { useEffect, useRef, type ReactNode } from "react";
import type React from "react";
import { estimateSuffix, type Measure } from "./estimate.ts";

/**
 * A dense, semantic data table (design 12.4: "semantic tables"; 12.3: counters exact, estimates
 * "est."). The screens render a lot of tabular data (per-route tables, source lists, findings), so
 * this one generic component gives them all the same accessible shape:
 *
 * - A real `<table>` with a `<caption>`, `<th scope="col">` headers and an optional `<th scope="row">`
 *   row header, so screen readers announce each cell with its column and (optionally) its row.
 * - A sticky header is opt-in for long tables; it uses CSS only, no JS scroll handling.
 * - An honest empty state: when there are no rows it renders a single explanatory message (design
 *   12.3 "empty states explain the cause") inside the table, not a bare header with no body.
 * - Per-column confidence: a column may be marked exact/estimate/unverifiable, adding the "est."
 *   affordance to its header so a reader knows the whole column's confidence at a glance.
 *
 * It is generic over the row type `T`; a column declares how to render its cell from a row. Cells
 * may return any `ReactNode` (a `StatusBadge`, a `Sparkline`, formatted text), so the table stays
 * presentational and screen-agnostic — it does no data fetching and no sorting of its own (a screen
 * sorts its rows before passing them, keeping this component simple and predictable).
 */

export interface Column<T> {
  /** Stable key for React and for `aria`. */
  readonly key: string;
  /** The visible column header. */
  readonly header: ReactNode;
  /** Render this column's cell for a row. */
  readonly cell: (row: T, rowIndex: number) => ReactNode;
  /** Mark this cell as a row header (`<th scope="row">`). At most one column should set this. */
  readonly isRowHeader?: boolean;
  /** `right` for numeric columns; defaults to `left`. */
  readonly align?: "left" | "right";
  /** Column-wide confidence; adds "est." to the header (design 12.3). */
  readonly measure?: Measure;
  /** Optional narrow-column hint for CSS (`data-width`). */
  readonly width?: "sm" | "md" | "lg";
}

export interface DenseTableProps<T> {
  /** Accessible caption describing the table. */
  readonly caption: ReactNode;
  /** Visually hide the caption while keeping it for assistive tech. Default false. */
  readonly captionHidden?: boolean;
  /** The columns, left to right. */
  readonly columns: readonly Column<T>[];
  /** The rows, already ordered/filtered by the caller. */
  readonly rows: readonly T[];
  /** A stable key for a row (for React reconciliation). Defaults to the row index. */
  readonly rowKey?: (row: T, index: number) => string;
  /** Shown when `rows` is empty — explains the cause (design 12.3). */
  readonly emptyMessage?: string;
  /** Keep the header fixed while the body scrolls. Default false. */
  readonly stickyHeader?: boolean;
  /**
   * When set, each row is clickable: the whole `<tr>` becomes an activatable control (pointer
   * cursor, hover, and keyboard — Enter/Space — with `aria-label` from {@link rowLabel}). Use for a
   * list whose row opens a detail view. A click on an interactive element inside a cell (a button or
   * link) is NOT treated as a row activation, so in-cell actions still work.
   */
  readonly onRowClick?: (row: T, index: number) => void;
  /** Accessible label for a clickable row (required UX when `onRowClick` is set). */
  readonly rowLabel?: (row: T, index: number) => string;
  /** The `rowKey` of a row to highlight and scroll into view (e.g. a deep-linked row). */
  readonly highlightRowKey?: string | null;
}

export function DenseTable<T>(props: DenseTableProps<T>): ReactNode {
  const { columns, rows } = props;
  const rowKey = props.rowKey ?? ((_row: T, index: number): string => String(index));
  const isEmpty = rows.length === 0;

  const highlightRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (props.highlightRowKey != null && highlightRef.current !== null) {
      highlightRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [props.highlightRowKey]);

  return (
    <table
      className="data-table data-table--dense"
      data-sticky={props.stickyHeader === true ? "true" : undefined}
    >
      <caption className={props.captionHidden === true ? "sr-only" : undefined}>
        {props.caption}
      </caption>
      <thead>
        <tr>
          {columns.map((col) => (
            <th
              key={col.key}
              scope="col"
              data-align={col.align ?? "left"}
              data-width={col.width}
            >
              {col.header}
              {col.measure !== undefined && (
                <span className="data-table__measure">{estimateSuffix(col.measure)}</span>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isEmpty ? (
          <tr className="data-table__empty-row">
            <td colSpan={columns.length}>
              <span className="data-table__empty">
                {props.emptyMessage ?? "Nothing to show in this range."}
              </span>
            </td>
          </tr>
        ) : (
          rows.map((row, rowIndex) => (
            <tr
              key={rowKey(row, rowIndex)}
              ref={props.highlightRowKey != null && rowKey(row, rowIndex) === props.highlightRowKey ? highlightRef : undefined}
              className={[
                props.onRowClick !== undefined ? "data-table__row--clickable" : "",
                props.highlightRowKey != null && rowKey(row, rowIndex) === props.highlightRowKey
                  ? "data-table__row--highlight"
                  : "",
              ]
                .filter(Boolean)
                .join(" ") || undefined}
              {...(props.onRowClick !== undefined
                ? {
                    tabIndex: 0,
                    "aria-label": props.rowLabel?.(row, rowIndex),
                    onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
                      // Let an interactive element inside a cell handle its own click.
                      if ((e.target as HTMLElement).closest("button, a, input, select, textarea")) return;
                      props.onRowClick?.(row, rowIndex);
                    },
                    onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.key !== "Enter" && e.key !== " ") return;
                      if ((e.target as HTMLElement).closest("button, a, input, select, textarea")) return;
                      e.preventDefault();
                      props.onRowClick?.(row, rowIndex);
                    },
                  }
                : {})}
            >
              {columns.map((col) => {
                const content = col.cell(row, rowIndex);
                if (col.isRowHeader === true) {
                  return (
                    <th key={col.key} scope="row" data-align={col.align ?? "left"}>
                      {content}
                    </th>
                  );
                }
                return (
                  <td key={col.key} data-align={col.align ?? "left"}>
                    {content}
                  </td>
                );
              })}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
